import { createBrowserRouter, type RouteObject } from 'react-router-dom';
import { ProtectedRoute } from '@/auth/ProtectedRoute';
import { LOCAL_AUTH } from '@/auth/authMode';
import { ShellLayout } from '@/components/ShellLayout';
import { LoginPage } from '@/features/auth/LoginPage';
import { CallbackPage } from '@/features/auth/CallbackPage';
import { SignInPage } from '@/features/auth/SignInPage';
import { SignUpPage } from '@/features/auth/SignUpPage';
import { WorkspaceLayout } from '@/features/workspace/WorkspaceLayout';
import { RootRedirect } from '@/features/workspace/RootRedirect';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { AgentsListPage } from '@/features/agents/AgentsListPage';
import { AgentLayout } from '@/features/agents/AgentLayout';
import { AgentConfigPage } from '@/features/agents/AgentConfigPage';
import { AgentHistoryPage } from '@/features/agents/AgentHistoryPage';
import { ChatPage } from '@/features/chat/ChatPage';
import { ConnectorsListPage } from '@/features/connectors/ConnectorsListPage';
import { ConnectorFormPage } from '@/features/connectors/ConnectorFormPage';
import { CatalogsListPage } from '@/features/catalogs/CatalogsListPage';
import { CatalogFormPage } from '@/features/catalogs/CatalogFormPage';
import { KnowledgeBasesListPage } from '@/features/knowledge/KnowledgeBasesListPage';
import { KnowledgeBaseDetailPage } from '@/features/knowledge/KnowledgeBaseDetailPage';
import { TeamPage } from '@/features/team/TeamPage';
import { BillingPage } from '@/features/billing/BillingPage';
import { NotFoundPage } from '@/features/misc/NotFoundPage';

// Public credential routes exist only in local auth mode.
const localAuthRoutes: RouteObject[] = LOCAL_AUTH
  ? [
      { path: '/signin', element: <SignInPage /> },
      { path: '/signup', element: <SignUpPage /> },
    ]
  : [];

// The whole authenticated SPA is nested under `/:workspaceSlug` (spec 0013,
// ADR-0003): the leading segment names the active workspace, and resources are
// addressed by slug beneath it. Public auth routes stay unprefixed. Static
// segments (login/signin/signup/auth) outrank the `:workspaceSlug` param, and
// the reserved-word guard keeps a workspace slug from ever shadowing them.
export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  { path: '/auth/callback', element: <CallbackPage /> },
  ...localAuthRoutes,
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <ShellLayout />,
        children: [
          { index: true, element: <RootRedirect /> },
          {
            path: ':workspaceSlug',
            element: <WorkspaceLayout />,
            children: [
              { index: true, element: <DashboardPage /> },
              { path: 'agents', element: <AgentsListPage /> },
              {
                path: 'agents/:agentRef',
                element: <AgentLayout />,
                children: [
                  { index: true, element: <AgentConfigPage /> },
                  { path: 'chat', element: <ChatPage /> },
                  { path: 'history', element: <AgentHistoryPage /> },
                ],
              },
              { path: 'connectors', element: <ConnectorsListPage /> },
              { path: 'connectors/new', element: <ConnectorFormPage /> },
              { path: 'connectors/:connectorRef', element: <ConnectorFormPage /> },
              { path: 'catalogs', element: <CatalogsListPage /> },
              { path: 'catalogs/new', element: <CatalogFormPage /> },
              { path: 'catalogs/:catalogRef', element: <CatalogFormPage /> },
              { path: 'knowledge', element: <KnowledgeBasesListPage /> },
              { path: 'knowledge/:baseRef', element: <KnowledgeBaseDetailPage /> },
              { path: 'settings/team', element: <TeamPage /> },
              { path: 'settings/billing', element: <BillingPage /> },
              { path: '*', element: <NotFoundPage /> },
            ],
          },
        ],
      },
    ],
  },
]);
